// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL;

// Application Constants
export const APP_NAME = 'Inventory Management System';
export const APP_VERSION = '1.0.0';

// Routes
export const ROUTES = {
  // Public Pages
  LANDING: '/',
  DOWNLOAD: '/download',

  // Authentication
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // Super Admin
  SUPER_ADMIN_LOGIN: '/super-admin/login',
  SUPER_ADMIN_DASHBOARD: '/super-admin/dashboard',
  SUPER_ADMIN_PROFILE: '/super-admin/profile',

  // Super Admin Tenants
  SUPER_ADMIN_TENANTS: '/super-admin/tenants',
  SUPER_ADMIN_USERS: '/super-admin/users',
  SUPER_ADMIN_USER_CREATE: '/super-admin/users/create',
  SUPER_ADMIN_USER_EDIT: '/super-admin/users/:id/edit',
  SUPER_ADMIN_USER_VIEW: '/super-admin/users/:id',
  SUPER_ADMIN_TENANT_CREATE: '/super-admin/tenants/new',
  SUPER_ADMIN_TENANT_EDIT: '/super-admin/tenants/:id/edit',
  SUPER_ADMIN_TENANT_DETAIL: '/super-admin/tenants/:id',
  SUPER_ADMIN_TENANT_DOCUMENTS: '/super-admin/tenants/:id/documents',
  SUPER_ADMIN_TENANT_BANK_DETAILS: '/super-admin/tenants/:id/bank-details',
  // NOTE: SUPER_ADMIN_TENANT_DEFAULT_CONFIG removed - feature_defaults table has been deleted

  // Dashboard
  DASHBOARD: '/dashboard',

  // Purchase Orders
  PURCHASE_ORDERS: '/purchase-orders',
  PO_CREATE: '/purchase-orders/new',
  PO_EDIT: '/purchase-orders/:id/edit',
  PO_DETAIL: '/purchase-orders/:id',

  // Inventory
  INVENTORY: '/inventory',
  INVENTORY_CREATE: '/inventory/new',
  INVENTORY_DETAIL: '/inventory/:id',

  // Suppliers
  SUPPLIERS: '/suppliers',
  SUPPLIER_CREATE: '/suppliers/new',
  SUPPLIER_DETAIL: '/suppliers/:id',
  SUPPLIER_DOCUMENTS: '/suppliers/:id/documents',

  // Machines
  MACHINES: '/machines',
  MACHINE_CREATE: '/machines/new',
  MACHINE_DETAIL: '/machines/:id',

  // Tenants
  TENANTS: '/tenants',
  TENANT_CREATE: '/tenants/new',
  TENANT_EDIT: '/tenants/:id/edit',
  TENANT_DETAIL: '/tenants/:id',
  TENANT_DOCUMENTS: '/tenants/:id/documents',
  TENANT_BANK_DETAILS: '/tenants/:id/bank-details',
  TENANT_DEFAULT_CONFIG: '/tenants/:id/default-config',
  MY_TENANT: '/my-tenant',

  // Documents
  DOCUMENTS: '/documents',
  DOCUMENTS_UPLOAD: '/documents/upload',

  // Bank Details
  BANK_DETAILS: '/bank-details',
  BANK_DETAILS_CREATE: '/bank-details/create',

  // Terms & Conditions
  TERMS: '/terms',
  TERMS_CREATE: '/terms/create',

  // Reports
  REPORTS: '/reports',
  REPORT_MACHINE_DETAIL: '/reports/machines/:id',

  // Configuration
  CONFIGURATION: '/configuration',
  // NOTE: FEATURE_CONFIG removed - feature_list table has been deleted
  // Users
  USERS: '/users',
  USER_CREATE: '/users/create',
  USER_EDIT: '/users/:id/edit',
  USER_DETAIL: '/users/:id',

  // Profile
  PROFILE: '/profile',

  // Supplier Response (public, no auth)
  SUPPLIER_RESPONSE: '/supplier-response/:token',

  // TEMPORARILY COMMENTED OUT - Notification system disabled
  // NOTIFICATIONS: '/notifications'
};

// PO Status Constants
export const PO_STATUS = {
  DRAFT: 'DRAFT',
  APPLIED: 'APPLIED',
  SENT_TO_SUPPLIER: 'SENT_TO_SUPPLIER',
  SUPPLIER_RESPONDED: 'SUPPLIER_RESPONDED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED'
};

// PO Status Labels
export const PO_STATUS_LABELS = {
  [PO_STATUS.DRAFT]: 'Draft',
  [PO_STATUS.APPLIED]: 'Applied',
  [PO_STATUS.SENT_TO_SUPPLIER]: 'Sent to Supplier',
  [PO_STATUS.SUPPLIER_RESPONDED]: 'Supplier Responded',
  [PO_STATUS.APPROVED]: 'Approved',
  [PO_STATUS.REJECTED]: 'Rejected',
  [PO_STATUS.CANCELLED]: 'Cancelled'
};

// Stock Level Constants
export const STOCK_LEVEL = {
  LOW: 'low',
  NORMAL: 'normal',
  OVERSTOCK: 'overstock'
};

// Stock Level Labels
export const STOCK_LEVEL_LABELS = {
  [STOCK_LEVEL.LOW]: 'Low Stock',
  [STOCK_LEVEL.NORMAL]: 'In Stock',
  [STOCK_LEVEL.OVERSTOCK]: 'Overstock'
};

// Document Owner Types
export const DOCUMENT_OWNER_TYPES = {
  TENANT: 'TENANT',
  SUPPLIER: 'SUPPLIER',
  PO: 'PURCHASE_ORDER'
};

// Bank Owner Types (matches database enum)
export const BANK_OWNER_TYPES = {
  TENANT: 'TENANT'
};

// Movement Types
export const MOVEMENT_TYPES = {
  IN: 'IN',
  USAGE: 'USAGE'
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  USER: 'user'
};

// Permissions
export const PERMISSIONS = {
  // Purchase Orders
  CREATE_PO: 'create_po',
  EDIT_PO: 'edit_po',
  DELETE_PO: 'delete_po',
  APPROVE_PO: 'approve_po',
  RECEIVE_GOODS: 'receive_goods',
  CONVERT_TO_BILL: 'convert_to_bill',

  // Inventory
  CREATE_INVENTORY: 'create_inventory',
  EDIT_INVENTORY: 'edit_inventory',
  DELETE_INVENTORY: 'delete_inventory',
  ADJUST_STOCK: 'adjust_stock',

  // Suppliers
  CREATE_SUPPLIER: 'create_supplier',
  EDIT_SUPPLIER: 'edit_supplier',
  DELETE_SUPPLIER: 'delete_supplier',

  // Users
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',

  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_DATA: 'export_data',

  // System
  MANAGE_SETTINGS: 'manage_settings'
};

// Table Pagination Options
export const PAGINATION_OPTIONS = [10, 25, 50, 100];

// File Upload
export const FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif'],
  PDF: ['application/pdf'],
  DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ALL: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
};

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',

  // Purchase Orders
  PURCHASE_ORDERS: '/purchase-orders',
  PURCHASE_ORDER: (id) => `/purchase-orders/${id}`,

  // Inventory
  INVENTORY_ITEMS: '/inventory-items',
  INVENTORY_ITEM: (id) => `/inventory-items/${id}`,

  // Suppliers
  SUPPLIERS: '/suppliers',
  SUPPLIER: (id) => `/suppliers/${id}`,
  SUPPLIER_SEARCH: '/suppliers/search',

  // Machines
  MACHINES: '/machines',
  MACHINE: (id) => `/machines/${id}`,
  MACHINE_SEARCH: '/machines/search',

  // Tenants
  TENANTS: '/tenants',
  TENANT: (id) => `/tenants/${id}`,
  TENANT_SEARCH: '/tenants/search',
  TENANT_COUNT: '/tenants/count',

  // Documents
  DOCUMENTS: '/documents',
  DOCUMENT: (id) => `/documents/${id}`,

  // Bank Details
  BANK_DETAILS: '/bank-details',
  BANK_DETAIL: (id) => `/bank-details/${id}`,

  // Terms & Conditions
  TERMS_AND_CONDITIONS: '/terms-and-conditions',
  TERMS_AND_CONDITION: (id) => `/terms-and-conditions/${id}`,


  // Reports
  REPORTS: '/reports',
  REPORT_SUMMARY: '/reports/summary',
  REPORT_MACHINE_EXPENSES: '/reports/machine-expenses',
  REPORT_MONTHLY_EXPENSES: '/reports/monthly-expenses',
  REPORT_MACHINE_EXPENSE_DETAIL: (machineId) => `/reports/machines/${machineId}/expenses`,

  // TEMPORARILY COMMENTED OUT - Notification system disabled
  // NOTIFICATIONS: '/notifications'
};

// Legacy constants for backward compatibility
export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};
